//////////////////////////////////
// IMPORTING MODULES
//////////////////////////////////

import { useState } from "react";
import axios from "axios";

const formFields = [
  {
    id: "firstName",
    placeholder: "Full Name",
    type: "text",
    googleFormEntryId: "entry.1782811584",
  },
  {
    id: "email",
    placeholder: "Email",
    type: "email",
    googleFormEntryId: "entry.1796655042",
  },
];

const GOOGLE_FORM_LINK =
  "https://docs.google.com/forms/d/e/1FAIpQLSepLul0w7QkBalvT2riMjYhek6lrxMIaRhJqEgtMDlHO6_Nvw/formResponse";
const GOOGLE_FORM_MESSAGE_ID = "entry.566797207";

//////////////////////////////////
// COMPONENT: CONTACT
//////////////////////////////////

const Contact = (): JSX.Element => {
  // Initialize form state
  const initialState = formFields.reduce((acc, field) => {
    acc[field.id] = "";
    return acc;
  }, {} as Record<string, string>);

  const [formValues, setFormValues] =
    useState<Record<string, string>>(initialState);
  const [messageValue, setMessageValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  // Handle input changes
  const handleInputChange = (id: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [id]: value }));
  };

  // Submit to Google Forms
  const sendMessage = () => {
    // Validate all fields
    for (const field of formFields) {
      if (!formValues[field.id].trim()) {
        alert(`Please enter your ${field.placeholder.toLowerCase()}.`);
        return;
      }
    }

    if (!validateEmail(formValues.email)) {
      alert("Please enter a valid email.");
      return;
    }

    if (!messageValue.trim()) {
      alert("Please enter a message.");
      return;
    }

    setLoading(true);
    const formData = new FormData();

    // Add form fields to FormData
    formFields.forEach(({ id, googleFormEntryId }) => {
      formData.append(googleFormEntryId, formValues[id]);
    });

    // Add message
    formData.append(
      GOOGLE_FORM_MESSAGE_ID,
      "Flight Trail Website: " + messageValue
    );

    // Submit to Google Forms
    axios
      .post(GOOGLE_FORM_LINK, formData)
      .then(() => setSubmitted(true))
      .catch(() => setSubmitted(true)) // Google Forms CORS workaround
      .finally(() => setLoading(false));
  };

  return (
    <div>
      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#141826]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-400 px-10">
              Have questions? We'd love to hear from you!
            </p>
          </div>
          <div className="bg-[#141826] backdrop-blur-sm p-8 pt-0 max-w-2xl mx-auto">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="bg-[#2ab8ff]/10 rounded-full p-4 mb-4">
                  <svg
                    className="h-12 w-12 text-[#2ab8ff]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold mb-2">
                  Message Sent!
                </h3>
                <p className="text-sm sm:text-base text-gray-400 text-center max-w-md">
                  Thank you for reaching out. We've received your message and
                  will get back to you as soon as possible.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
                className="space-y-6 bg-[#141826]"
              >
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formValues.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="w-full px-4 py-3 bg-[#0e1220] border border-gray-700 rounded-lg focus:outline-none focus:border-[#2ab8ff] transition-colors text-white"
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formValues.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-4 py-3 bg-[#0e1220] border border-gray-700 rounded-lg focus:outline-none focus:border-[#2ab8ff] transition-colors text-white"
                    placeholder="Email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    value={messageValue}
                    onChange={(e) => setMessageValue(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 bg-[#0e1220] border border-gray-700 rounded-lg focus:outline-none focus:border-[#2ab8ff] transition-colors text-white resize-none"
                    placeholder="Your message..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-8 py-4 bg-gradient-to-r bg-[#2ab8ff]  text-black rounded-lg hover:shadow-lg  transition-all font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

//////////////////////////////////
// EXPORTING CONTACT
//////////////////////////////////

export default Contact;
